// src/components/configuration/sections/GBPSettingsShadcn.jsx
import React, { useState, useEffect } from 'react';
import { AlertCircle, CheckCircle2, XCircle, MapPin, Star, ExternalLink } from 'lucide-react';

// shadcn components
import { Alert, AlertDescription } from '../../ui/alert';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../../ui/card';
import { Button } from '../../ui/button';
import { Checkbox } from '../../ui/checkbox';
import { Spinner } from '../../ui/spinner';

// Keep existing PageContainer for headers
import PageContainer from '../../ui/layout/PageContainer/PageContainer';

// GBP hook
import { useGBP } from '../../../hooks/useGBP';

/**
 * GBP Settings Component - Handles OAuth flow and location selection
 *
 * IMPORTANT: This component handles OAuth redirects from Google
 * - On mount, checks for ?success=true&step=select_locations query params
 * - Multi-step flow: Disconnected → OAuth → Select Locations → Connected
 */
const GBPSettingsShadcn = () => {
  const {
    // Connection status
    gbpConnection,
    gbpConnectionLoading,
    gbpConnectionError,
    isConnected,
    connected,
    currentStatus,
    statusMessage,
    tokenExpired,
    requiresSetup,

    // OAuth flow
    oauthUrl,
    oauthInitiating,

    // Accounts & Locations
    accounts,
    accountsLoading,
    accountsError,
    locations,
    locationsLoading,
    locationsError,
    selectedLocations,
    savingLocations,
    connectedLocations,
    primaryLocation,
    accountName,

    // Operations
    loadGBPConnectionStatus,
    startGBPOAuth,
    fetchGBPAccounts,
    fetchGBPLocations,
    saveGBPLocations,
    setSelectedLocations,
    disconnectGBP,

    // Utility
    clearGBPConnectionError,
    clearAccountsError,
    clearLocationsError
  } = useGBP();

  // Local UI state
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);
  const [currentStep, setCurrentStep] = useState('loading'); // loading, disconnected, select_locations, connected
  const [oauthError, setOauthError] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState(null);

  // ================================
  // OAUTH REDIRECT HANDLING
  // ================================

  /**
   * Check for OAuth callback query params on mount
   * Backend redirects to: /settings/integrations/gbp?success=true&step=select_locations
   * Or on error: /settings/integrations/gbp?error=error_code&message=error_message
   */
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const success = urlParams.get('success');
    const error = urlParams.get('error');
    const errorMessage = urlParams.get('message');
    const step = urlParams.get('step');

    if (success === 'true') {
      console.log('✅ GBPSettings: OAuth success detected, step:', step);

      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);

      // If step is select_locations, fetch accounts
      if (step === 'select_locations') {
        setCurrentStep('select_locations');
        fetchGBPAccounts();
      }
    } else if (error) {
      console.error('❌ GBPSettings: OAuth error detected:', error, errorMessage);
      setOauthError(errorMessage || error);

      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [fetchGBPAccounts]);

  // ================================
  // LOAD CONNECTION STATUS ON MOUNT
  // ================================

  useEffect(() => {
    console.log('🔌 GBPSettings: Loading connection status on mount');
    loadGBPConnectionStatus();
  }, [loadGBPConnectionStatus]);

  // ================================
  // DETERMINE CURRENT STEP
  // ================================

  useEffect(() => {
    if (gbpConnectionLoading) {
      setCurrentStep('loading');
      return;
    }

    // If we already determined step from OAuth callback, don't override
    if (currentStep === 'select_locations') {
      return;
    }

    if (connected) {
      setCurrentStep('connected');
    } else {
      setCurrentStep('disconnected');
    }
  }, [connected, gbpConnectionLoading, currentStep]);

  // ================================
  // OAUTH URL GENERATED - REDIRECT
  // ================================

  useEffect(() => {
    if (oauthUrl) {
      console.log('🚀 GBPSettings: OAuth URL generated, redirecting to Google...');
      // Redirect user to Google OAuth
      window.location.href = oauthUrl;
    }
  }, [oauthUrl]);

  // ================================
  // AUTO-SELECT ACCOUNT IF ONLY ONE
  // ================================

  useEffect(() => {
    if (accounts && accounts.length === 1 && !selectedAccount) {
      console.log('📋 GBPSettings: Only one account found, auto-selecting');
      setSelectedAccount(accounts[0]);
      fetchGBPLocations(accounts[0].name);
    }
  }, [accounts, selectedAccount, fetchGBPLocations]);

  // ================================
  // HANDLERS
  // ================================

  const handleStartOAuth = () => {
    clearGBPConnectionError();
    setOauthError(null);
    startGBPOAuth();
  };

  const handleAccountSelect = (account) => {
    setSelectedAccount(account);
    clearLocationsError();
    fetchGBPLocations(account.name);
  };

  const handleLocationToggle = (location) => {
    setSelectedLocations(
      selectedLocations.some(loc => loc.name === location.name)
        ? selectedLocations.filter(loc => loc.name !== location.name)
        : [...selectedLocations, location]
    );
  };

  const handleSaveLocations = () => {
    if (selectedLocations.length === 0) {
      return;
    }
    saveGBPLocations(selectedLocations);
    setCurrentStep('connected');
    // Reload connection status to get updated data
    setTimeout(() => loadGBPConnectionStatus(), 1000);
  };

  const handleDisconnect = () => {
    disconnectGBP();
    setShowDisconnectConfirm(false);
    setCurrentStep('disconnected');
  };

  // ================================
  // RENDER HELPERS
  // ================================

  const renderLoading = () => (
    <div className="flex items-center justify-center py-12">
      <div className="text-center">
        <Spinner className="size-12 text-blue-600 mx-auto mb-4" />
        <p className="text-gray-600">Loading GBP connection status...</p>
      </div>
    </div>
  );

  const renderDisconnectedState = () => (
    <div className="space-y-6">
      {/* Error Alert */}
      {(oauthError || gbpConnectionError) && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {oauthError || gbpConnectionError}
          </AlertDescription>
        </Alert>
      )}

      {/* Benefits Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Star className="h-5 w-5 text-yellow-500" />
            Connect Google Business Profile
          </CardTitle>
          <CardDescription>
            Manage your Google reviews and business information in one place
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium">Reply to customer reviews</p>
                <p className="text-sm text-gray-600">Respond to Google reviews directly from your dashboard</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium">Track review sentiment</p>
                <p className="text-sm text-gray-600">Monitor your ratings and customer feedback over time</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium">Send automated review requests</p>
                <p className="text-sm text-gray-600">Generate review links for email and SMS campaigns</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium">Manage multiple locations</p>
                <p className="text-sm text-gray-600">Handle reviews for all your business locations</p>
              </div>
            </div>
          </div>

          <Button
            onClick={handleStartOAuth}
            disabled={oauthInitiating}
            className="w-full mt-6"
            size="lg"
          >
            {oauthInitiating ? (
              <>
                <Spinner className="size-4 mr-2" />
                Connecting...
              </>
            ) : (
              <>
                Connect with Google
                <ExternalLink className="ml-2 h-4 w-4" />
              </>
            )}
          </Button>
        </CardContent>
      </Card>
    </div>
  );

  const renderLocationSelection = () => (
    <div className="space-y-6">
      {/* Success Alert */}
      <Alert>
        <CheckCircle2 className="h-4 w-4 text-green-600" />
        <AlertDescription>
          Successfully connected to Google! {accountName && `Signed in as ${accountName}`}
        </AlertDescription>
      </Alert>

      {/* Error Alert */}
      {(accountsError || locationsError) && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {accountsError || locationsError}
          </AlertDescription>
        </Alert>
      )}

      {/* Account Selection (if multiple accounts) */}
      {accounts && accounts.length > 1 && (
        <Card>
          <CardHeader>
            <CardTitle>Step 1: Select Your Google Account</CardTitle>
            <CardDescription>Choose which Google Business Profile account to use</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {accounts.map((account) => (
                <button
                  key={account.name}
                  onClick={() => handleAccountSelect(account)}
                  className={`w-full p-4 text-left border rounded-lg transition-colors ${
                    selectedAccount?.name === account.name
                      ? 'border-blue-600 bg-blue-50'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <p className="font-medium">{account.accountName || account.name}</p>
                  <p className="text-sm text-gray-600">{account.name}</p>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Location Selection */}
      {selectedAccount && (
        <Card>
          <CardHeader>
            <CardTitle>
              {accounts.length > 1 ? 'Step 2: ' : ''}Select Your Business Locations
            </CardTitle>
            <CardDescription>
              Choose which locations you want to manage. You can change this later.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {locationsLoading ? (
              <div className="flex items-center justify-center py-8">
                <Spinner className="size-8 text-blue-600" />
              </div>
            ) : locations.length === 0 ? (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  No locations found for this account. Make sure you have a Google Business Profile set up.
                </AlertDescription>
              </Alert>
            ) : (
              <>
                <div className="space-y-3">
                  {locations.map((location) => (
                    <div
                      key={location.name}
                      className="flex items-start gap-3 p-4 border rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
                      onClick={() => handleLocationToggle(location)}
                    >
                      <Checkbox
                        checked={selectedLocations.some(loc => loc.name === location.name)}
                        onCheckedChange={() => handleLocationToggle(location)}
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-gray-500" />
                          <p className="font-medium">{location.locationName}</p>
                          {location.verified && (
                            <CheckCircle2 className="h-4 w-4 text-green-600" title="Verified" />
                          )}
                        </div>
                        {location.address && (
                          <p className="text-sm text-gray-600 mt-1">
                            {location.address.addressLines?.[0]}, {location.address.locality}, {location.address.administrativeArea}
                          </p>
                        )}
                        {location.reviewStats && (
                          <div className="flex items-center gap-2 mt-2 text-sm">
                            <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                            <span className="font-medium">{location.reviewStats.averageRating || 0}</span>
                            <span className="text-gray-600">({location.reviewStats.totalReviewCount || 0} reviews)</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="flex gap-3 mt-6">
                  <Button
                    onClick={handleSaveLocations}
                    disabled={selectedLocations.length === 0 || savingLocations}
                    className="flex-1"
                  >
                    {savingLocations ? (
                      <>
                        <Spinner className="size-4 mr-2" />
                        Saving...
                      </>
                    ) : (
                      `Save ${selectedLocations.length} Location${selectedLocations.length !== 1 ? 's' : ''}`
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setCurrentStep('disconnected')}
                  >
                    Cancel
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );

  const renderConnectedState = () => (
    <div className="space-y-6">
      {/* Token Expired Warning */}
      {tokenExpired && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Your Google Business Profile connection has expired. Please reconnect to continue managing reviews.
          </AlertDescription>
        </Alert>
      )}

      {/* Connection Status Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-green-600" />
            Connected to Google Business Profile
          </CardTitle>
          <CardDescription>
            {accountName && `Signed in as ${accountName}`}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Connected Locations */}
          <div>
            <h3 className="font-medium mb-3">Connected Locations ({connectedLocations.length})</h3>
            <div className="space-y-2">
              {connectedLocations.map((location) => (
                <div key={location.name} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <MapPin className="h-4 w-4 text-gray-500" />
                  <div className="flex-1">
                    <p className="font-medium text-sm">{location.locationName}</p>
                    {location.reviewStats && (
                      <div className="flex items-center gap-2 mt-1">
                        <Star className="h-3 w-3 text-yellow-500 fill-yellow-500" />
                        <span className="text-xs text-gray-600">
                          {location.reviewStats.averageRating || 0} ({location.reviewStats.totalReviewCount || 0} reviews)
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t">
            <Button
              variant="outline"
              onClick={() => window.location.href = '/reviews'}
              className="flex-1"
            >
              Open Reviews Dashboard
            </Button>
            <Button
              variant="destructive"
              onClick={() => setShowDisconnectConfirm(true)}
              disabled={disconnecting}
            >
              {disconnecting ? 'Disconnecting...' : 'Disconnect'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Disconnect Confirmation */}
      {showDisconnectConfirm && (
        <Card className="border-red-200 bg-red-50">
          <CardHeader>
            <CardTitle className="text-red-900">Disconnect Google Business Profile?</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                This will remove access to your Google Business Profile and stop syncing reviews.
                You can reconnect at any time.
              </AlertDescription>
            </Alert>
            <div className="flex gap-3">
              <Button
                variant="destructive"
                onClick={handleDisconnect}
                disabled={disconnecting}
                className="flex-1"
              >
                Yes, Disconnect
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowDisconnectConfirm(false)}
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );

  // ================================
  // MAIN RENDER
  // ================================

  return (
    <PageContainer
      title="Google Business Profile"
      subtitle="Manage your Google reviews and business information"
    >
      <div className="max-w-3xl">
        {currentStep === 'loading' && renderLoading()}
        {currentStep === 'disconnected' && renderDisconnectedState()}
        {currentStep === 'select_locations' && renderLocationSelection()}
        {currentStep === 'connected' && renderConnectedState()}
      </div>
    </PageContainer>
  );
};

export default GBPSettingsShadcn;
